"use client";

import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import PageHeader from "@/components/shared/PageHeader";
import GlassPanel from "@/components/dashboard/GlassPanel";
import Footer from "@/components/layout/Footer";
import { InstituteList } from "@/components/institutes/InstituteList";
import { InstituteFilters } from "@/components/institutes/InstituteFilters";
import { getPublicInstitutes } from "@/lib/api/institute";
import { useTaxonomyCategories } from "@/lib/taxonomy-service";

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedValue(value), delay);
    return () => window.clearTimeout(timeout);
  }, [value, delay]);

  return debouncedValue;
}

export default function PublicInstitutesPage() {
  const [countryId, setCountryId] = useState("");
  const [stateId, setStateId] = useState("");
  const [cityId, setCityId] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const { countries, cities } = useTaxonomyCategories(
    "countries",
    "states",
    "cities"
  );
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);

  const selectedCountry = useMemo(
    () => countries.find((item) => String(item.id) === countryId),
    [countries, countryId]
  );
  const selectedCity = useMemo(
    () => cities.find((item) => String(item.id) === cityId),
    [cities, cityId]
  );

  const cityParam = selectedCity?.label || "";
  const countryParam = selectedCountry?.label || "";

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [
      "public-institutes",
      debouncedSearch,
      cityParam,
      countryParam,
      page,
    ],
    queryFn: () =>
      getPublicInstitutes({
        search: debouncedSearch || undefined,
        city: cityParam || undefined,
        country: countryParam || undefined,
        page,
        pageSize: PAGE_SIZE,
      }),
    staleTime: 1000 * 60 * 2,
  });

  const institutes = data?.results ?? [];
  const totalPages = data?.totalPages ?? 1;

  const handleCountryChange = (value: string) => {
    setCountryId(value);
    setStateId("");
    setCityId("");
    setPage(1);
  };

  const handleStateChange = (value: string) => {
    setStateId(value);
    setCityId("");
    setPage(1);
  };

  const handleCityChange = (value: string) => {
    setCityId(value);
    setPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  return (
    <>
      <div className="space-y-6">
        <PageHeader
          title="Browse Institutes"
          subtitle="Explore approved maritime training institutes"
        />

        <GlassPanel className="p-5">
          <InstituteFilters
            countryId={countryId}
            onCountryChange={handleCountryChange}
            stateId={stateId}
            onStateChange={handleStateChange}
            cityId={cityId}
            onCityChange={handleCityChange}
            search={search}
            onSearchChange={handleSearchChange}
          />
        </GlassPanel>

        <InstituteList
          institutes={institutes}
          isLoading={isLoading}
          error={error as Error | null}
          onRefetch={refetch}
          count={data?.count ?? 0}
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      </div>
      <Footer />
    </>
  );
}
